// The holding page a signed-in but not-yet-approved (or blocked) non-admin
// lands on. Every protected route's `requireApproved()` redirects here; this
// page itself stays reachable by a pending user (router.tsx must NOT wrap it),
// else the redirect deadlocks.
//
// Tailwind shell, mirroring SignInPage (the other unauthenticated-shell page),
// so a pending user sees the same chrome they just came from.

import { useState } from "react";
import { z } from "zod";

import { invalidateMe } from "../auth/useAuth";
import { useT } from "../i18n/index.js";
import { httpPost } from "../lib/http";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { HeroMark } from "../ui/HeroMark";

const LogoutResponseSchema = z.object({ ok: z.boolean() });

export function PendingApprovalPage({
  onRecheck,
}: {
  /** Re-run the approval check (route loader re-fetches /auth/me). */
  readonly onRecheck: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);

  const signOut = async (): Promise<void> => {
    setBusy(true);
    try {
      await httpPost("/auth/logout", {}, LogoutResponseSchema);
    } catch {
      // Best-effort — the cookie is cleared server-side regardless; fall through.
    }
    // Hard navigation so the SPA re-evaluates auth from scratch. The shared
    // `/auth/me` answer goes first, for a page the browser restores from its
    // back/forward cache instead of reloading.
    invalidateMe();
    window.location.assign("/sign-in");
  };

  return (
    <div className="relative flex min-h-full items-center justify-center p-6">
      <HeroMark />
      <Card className="w-full max-w-md p-6">
        <h1 className="mb-2 text-xl font-semibold">{t("access.pending.title")}</h1>
        <p className="mb-6 text-sm text-ink-muted">{t("access.pending.body")}</p>
        <div className="flex items-center gap-3">
          <Button onClick={onRecheck}>{t("access.pending.recheck")}</Button>
          <button
            type="button"
            className="text-sm text-ink-subtle hover:text-ink-3 disabled:opacity-50"
            onClick={() => void signOut()}
            disabled={busy}
          >
            {t("access.pending.signOut")}
          </button>
        </div>
      </Card>
    </div>
  );
}
